import * as Schema from "effect/Schema";

export const AgentNotificationSource = Schema.Literals(["inspector", "insight", "workflow"]);
/** App-owned controls: agents name a target, never a URL, label, theme or HTML. */
export const NotificationFindingAction = Schema.Struct({
  kind: Schema.Literal("finding"),
  findingId: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(160)),
});
export const NotificationSuggestionAction = Schema.Struct({
  kind: Schema.Literal("suggestion"),
  recommendationId: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(160)),
});
export const NotificationMonitoringAction = Schema.Struct({ kind: Schema.Literal("monitoring") });
/** A workflow's Notify step: the run it is about (AMO-166). */
export const NotificationWorkflowRunAction = Schema.Struct({
  kind: Schema.Literal("workflow-run"),
  workflowId: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(160)),
  runId: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(160)),
});
export const NotificationAction = Schema.Union([
  NotificationFindingAction,
  NotificationSuggestionAction,
  NotificationMonitoringAction,
  NotificationWorkflowRunAction,
]);
/**
 * The thread a workflow Notify step's notice is filed under: not a chat, a
 * key for the run, so the reader is never offered a chat that does not exist.
 */
export const WORKFLOW_RUN_NOTICE_THREAD_PREFIX = "workflow-run:";
export const NotificationCategory = Schema.Literals([
  "finding",
  "suggestion",
  "monitoring",
  "research",
  "attention",
]);
export const NotificationContent = {
  category: Schema.optionalKey(NotificationCategory),
  summary: Schema.optionalKey(Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(500))),
  actions: Schema.optionalKey(Schema.Array(NotificationAction).check(Schema.isMaxLength(4))),
};
export const AgentNotificationRequest = Schema.Struct({
  source: AgentNotificationSource,
  ...NotificationContent,
  title: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(160)),
  body: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(6000)),
  severity: Schema.Literals(["high", "medium", "low"]),
  deduplicationKey: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(120)),
});
export type AgentNotificationRequest = typeof AgentNotificationRequest.Type;

export const InboxAgentNotification = Schema.Struct({
  kind: Schema.Literal("agent-notification"),
  id: Schema.String,
  createdAt: Schema.String,
  source: AgentNotificationSource,
  ...NotificationContent,
  title: Schema.String,
  body: Schema.String,
  severity: Schema.Literals(["high", "medium", "low"]),
  organizationId: Schema.String,
  organizationName: Schema.String,
  organizationSlug: Schema.String,
  threadId: Schema.String,
});
export type InboxAgentNotification = typeof InboxAgentNotification.Type;

/**
 * A workflow run waiting at an Approval step that names the reader (and was
 * not started by them). `createdAt` is when it began waiting; the client
 * brings it back as unread each working morning while it still waits.
 */
export const InboxWorkflowApproval = Schema.Struct({
  kind: Schema.Literal("workflow-approval"),
  id: Schema.String,
  createdAt: Schema.String,
  organizationId: Schema.String,
  organizationName: Schema.String,
  organizationSlug: Schema.String,
  workflowId: Schema.String,
  workflowName: Schema.String,
  runId: Schema.String,
  nodeId: Schema.String,
  attempt: Schema.Number,
  stepLabel: Schema.String,
  startedByEmail: Schema.NullOr(Schema.String),
  /** `checklist`: a manual step waiting to be marked done (AMO-307); absent for an approval and from older servers. */
  stepKind: Schema.optional(Schema.Literals(["approval", "checklist"])),
});
export type InboxWorkflowApproval = typeof InboxWorkflowApproval.Type;

/** Invitations come from pending membership records, never agent-generated content. */
export const InboxInvitationItem = Schema.Struct({
  kind: Schema.Literal("invitation"),
  id: Schema.String,
  createdAt: Schema.String,
  organizationName: Schema.String,
  organizationSlug: Schema.String,
  role: Schema.Literals(["admin", "member"]),
  expiresAt: Schema.String,
  /**
   * Who sent it, as a display name or address. Optional: older servers do not
   * send it, and a deleted inviter leaves nothing to show — the invitation is
   * still deliverable without it.
   */
  invitedBy: Schema.optionalKey(Schema.String),
});
export type InboxInvitationItem = typeof InboxInvitationItem.Type;
